/**
 * EN↔KO Language Pair Definition
 *
 * buildProfile() generates directional profiles:
 *   enKo = KO native → learn EN (UI in Korean)
 *   koEn = EN native → learn KO (UI in English)
 *
 * Phonetic: Korean learners read Hangul directly, so phonetic type is 'none'.
 * detectScript: covers Hangul syllables, jamo, and compatibility jamo
 */

import type { LanguagePairDefinition, LanguageProfile } from '../types'
import { buildProfile } from '../build-profile'

const PAIR: LanguagePairDefinition = {
  id: 'en-ko',
  languages: {
    en: {
      lang: 'en',
      langName: 'English',
      shortCode: 'EN',
      scriptSample: 'Hello',
      phonetic: {
        type: 'ipa',
        fields: [
          { key: 'phonetic_us', label: 'US' },
          { key: 'phonetic_uk', label: 'UK' },
        ],
      },
      tts: {
        voices: [
          { accent: 'us', lang: 'en-US' },
          { accent: 'uk', lang: 'en-GB' },
        ],
        detectScript: /^$/,
      },
      ui: {
        inputPlaceholder: 'Enter words, phrases, or questions...',
        headingText: 'What do you want to learn today?',
        intentChips: [
          { label: 'List', intent: 'BATCH_LIST', suffix: 'how to say in Korean' },
          { label: 'Compare', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in English.',
      },
    },
    ko: {
      lang: 'ko',
      langName: 'Korean',
      shortCode: 'KO',
      scriptSample: '안녕하세요',
      phonetic: {
        type: 'none',
        fields: [],
      },
      tts: {
        voices: [
          { accent: 'ko', lang: 'ko-KR' },
        ],
        detectScript: /[\uAC00-\uD7AF\u1100-\u11FF\u3130-\u318F]/,
      },
      ui: {
        inputPlaceholder: '단어, 문장 또는 질문을 입력하세요...',
        headingText: '오늘 무엇을 배우고 싶으신가요?',
        intentChips: [
          { label: '목록', intent: 'BATCH_LIST', suffix: '영어로 어떻게 말해' },
          { label: '비교', intent: 'CONFUSABLE_PAIRS', type: 'compare', separator: 'vs' },
        ],
      },
      promptConfig: {
        classifierHint: 'User may input in Korean (Hangul).',
        languageHints: [
          'For verbs, show the dictionary form (기본형) and common conjugated forms in collocations.',
          'Indicate speech level in example: register:casual (반말), register:polite (해요체), register:formal (하십시오체).',
          'Include the correct particle in example sentences (은/는, 이/가, 을/를, 에, 에서).',
          'For Sino-Korean words, include Hanja etymology in "definition" when it aids memory.',
          'Leave phonetic fields empty — Korean learners read Hangul directly.',
        ].join('\n'),
      },
      cardMeta: {
        keys: ['hanja', 'speechLevel'],
        promptDescription: `"card_meta" (JSON object, required): Korean-specific metadata:
- "hanja": Chinese character etymology. Only for Sino-Korean words.
- "speechLevel": formality level (e.g. "해요체", "하십시오체", "반말"). Only when relevant.`,
      },
    },
  },
}

export const enKoPair = PAIR
export const enKo: LanguageProfile = buildProfile(PAIR, 'en')
export const koEn: LanguageProfile = buildProfile(PAIR, 'ko')
